import { useState } from "react";
import { Box, Typography, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { CHANGELOG, APP_VERSION } from "../../changelog";

const SEEN_KEY = "hw-seen-version";
const primary = "#1535BD";

/**
 * A small version label that opens the changelog when clicked. When the current
 * release hasn't been opened yet, it glows to draw the eye on first page load;
 * once viewed it reverts to a plain, muted "v2.0".
 */
const VersionBadge = () => {
  const [open, setOpen] = useState(false);
  // "New" until the user has opened the changelog for the current version.
  const [isNew, setIsNew] = useState(() => {
    try {
      return localStorage.getItem(SEEN_KEY) !== APP_VERSION;
    } catch {
      return false;
    }
  });

  const handleOpen = () => {
    setOpen(true);
    // Mark seen in UI regardless of whether persistence succeeds (e.g. storage disabled).
    setIsNew(false);
    try {
      localStorage.setItem(SEEN_KEY, APP_VERSION);
    } catch {
      /* storage unavailable — badge simply re-glows next session */
    }
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        aria-label={`Version ${APP_VERSION}. See what's new.`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOpen();
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          userSelect: "none",
          borderRadius: "999px",
          fontFamily: "Mulish, sans-serif",
          transition: "color 0.2s, background-color 0.2s, padding 0.2s",
          ...(isNew
            ? {
                color: primary,
                bgcolor: "rgba(21,53,189,0.08)",
                border: "1px solid rgba(21,53,189,0.18)",
                px: 1.1,
                py: 0.4,
                animation: "versionGlow 2.2s ease-in-out infinite",
              }
            : {
                color: "#aab1bd",
                bgcolor: "transparent",
                border: "1px solid transparent",
                px: 0.5,
                py: 0.25,
                "&:hover": { color: "#6b7280" },
              }),
        }}
      >
        {isNew && <AutoAwesomeOutlinedIcon sx={{ fontSize: 13 }} />}
        <Typography
          component="span"
          sx={{ fontSize: "0.72rem", fontWeight: isNew ? 800 : 600, lineHeight: 1, color: "inherit" }}
        >
          v{APP_VERSION}
        </Typography>
        {isNew && (
          <Typography
            component="span"
            sx={{ fontSize: "0.72rem", fontWeight: 800, lineHeight: 1, color: "inherit" }}
          >
            · What's new
          </Typography>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", mt: "env(safe-area-inset-top, 24px)" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(21,53,189,0.08)",
            userSelect: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeOutlinedIcon sx={{ color: primary, fontSize: 20 }} />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "1.05rem",
                color: primary,
                fontFamily: "Mulish, sans-serif",
                letterSpacing: "-0.01em",
              }}
            >
              What's New
            </Typography>
          </Box>
          <IconButton size="small" aria-label="Close" onClick={() => setOpen(false)} sx={{ color: "rgba(0,0,0,0.35)", mr: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 2.5, pb: 3, px: 3 }}>
          {CHANGELOG.map((release, i) => (
            <Box key={release.version} sx={{ mb: i === CHANGELOG.length - 1 ? 0 : 3 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    color: primary,
                    bgcolor: "rgba(21,53,189,0.1)",
                    borderRadius: "6px",
                    px: 0.8,
                    py: 0.25,
                    fontFamily: "Mulish, sans-serif",
                  }}
                >
                  v{release.version}
                </Box>
                {release.date && (
                  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", fontFamily: "Mulish, sans-serif" }}>
                    {release.date}
                  </Typography>
                )}
              </Box>

              {release.title && (
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.95rem",
                    color: "#1a1a1a",
                    mb: 1,
                    fontFamily: "Mulish, sans-serif",
                  }}
                >
                  {release.title}
                </Typography>
              )}

              <Box component="ul" sx={{ m: 0, pl: 2.2, display: "flex", flexDirection: "column", gap: 0.6 }}>
                {release.changes.map((change, j) => (
                  <Typography
                    key={j}
                    component="li"
                    sx={{
                      fontSize: "0.85rem",
                      color: "#444",
                      lineHeight: 1.55,
                      fontFamily: "Mulish, sans-serif",
                      "&::marker": { color: primary },
                    }}
                  >
                    {change}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VersionBadge;
